import { useEffect } from 'react';

// ── HOLD THE PAGE STILL BEHIND AN OVERLAY ────────────────────────────────────
//
// `overflow: hidden` on the body is enough on desktop and NOT enough on iOS.
// When the keyboard opens, or when a fixed overlay is dragged, Safari scrolls
// the DOCUMENT; overflow:hidden does not stop it, and because an overlay is
// position:fixed against the LAYOUT viewport it stays put while the page slides
// underneath. That is the drift.
//
// The fix is to make the document unscrollable in a way iOS respects: pin the
// body at its current offset, then put the scroll back on close. Nothing can
// scroll, so nothing can drift.
//
// Extracted from ChatOverlay when the photo lightbox needed the same thing —
// there the page behind was scrolling freely under an open photo. One copy, so
// the StrictMode subtlety below cannot be fixed in one place and missed in the
// other.
export default function useScrollLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    const body = document.body;

    // Pinning the body sets the page scroll to 0. If this effect runs twice —
    // React StrictMode double-invokes in development, and any remount does the
    // same — the second run would read that 0 and "restore" the user to the top
    // of the page on close. So when the body is ALREADY pinned, the real
    // position is the offset we parked in `top`, not what the window reports.
    const alreadyPinned = body.style.position === 'fixed';
    const scrollY = alreadyPinned
      ? Math.abs(parseInt(body.style.top || '0', 10)) || 0
      : window.scrollY;

    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      Object.assign(body.style, prev);
      // Pinning threw the scroll position away; put it back, or closing jumps
      // the user to the top of whatever page they were on.
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}
